import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getRoleNamesByIds, updateRole} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    DEFAULT_ROLE,

    ROLE_DESCRIPTION_CONTROL,
    ROLE_DESCRIPTION_REQUIRED_ERROR,
    ROLE_DESCRIPTION_REQUIRED_MESSAGE,

    ROLE_NAME_CONTROL,
    ROLE_NAME_REQUIRED_ERROR,
    ROLE_NAME_REQUIRED_MESSAGE,
} from './constants';

import type {Role} from 'src/openapi/admin/models/Role';
import type {ErrorPayload} from 'src/typedef';

type Props = {
    roleId: string,
};

export const useUpdateRole = ({roleId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        const response = await apiDispatch(getRoleNamesByIds({requestBody: [roleId]}));
        if (response.payload.success) {
            if (!response.payload.data[roleId]) {
                return Promise.resolve({type: '', payload: {
                    success: false,
                    error: true,
                    data: {message: 'Role not found', status: 404},
                } as ErrorPayload});
            }

            const payload: {success: true, error: false, data: Role} = {
                success: true,
                error: false,
                data: response.payload.data[roleId],
            };
            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [roleId, apiDispatch]);

    const saveDispatchAction = useCallback((updateRoleRequest: Role, signal: AbortSignal) => apiDispatch(
        updateRole({roleId, updateRoleRequest}, {signal}),
    ), [roleId, apiDispatch]);

    const editValidation = useCallback((data: Role) => {
        if (data.name) {
            removeError(ROLE_NAME_CONTROL);
        }

        if (data.description) {
            removeError(ROLE_DESCRIPTION_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: Role) => {
        let valid = true;

        if (!data.name) {
            addError({
                controlId: ROLE_NAME_CONTROL,
                code: ROLE_NAME_REQUIRED_ERROR,
                message: ROLE_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.description) {
            addError({
                controlId: ROLE_DESCRIPTION_CONTROL,
                code: ROLE_DESCRIPTION_REQUIRED_ERROR,
                message: ROLE_DESCRIPTION_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    return useCommonDataHook({
        initValue: DEFAULT_ROLE,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
